import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe, Loader2, Moon, Palette, PlugZap, Shield, Store, Sun, UserRound, Wallet } from 'lucide-react';
import { useTheme } from '@/components/theme/theme-provider';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { persistor } from '@/app/store';
import { logoutAndClear, setUserLanguage, updatePreferredLanguage } from '@/features/auth/authSlice';
import { selectHasMeaningfulItems } from '@/features/order/orderSlice';
import ConfirmAlertDialog from '@/components/ui/confirm-alert-dialog';
import { useNavigate } from 'react-router-dom';
import Logo from '@/assets/images/tulasi-circle.png';
import LogoWhite from '@/assets/images/tulasi-circle_white.png';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import { loadWalletSummary, selectWalletBalance, selectWalletSummary } from '@/features/wallet/walletSlice';
import { useTranslationLocales } from '@/hooks/useTranslationLocales';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { WORKSPACE_CANVAS_MODES } from '@/components/workspace/workspaceMode';

const DEFAULT_LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English' },
  { code: 'uk', name: 'Українська' },
] as const;

const normalizeLanguageCode = (code: string) => code.trim().toLowerCase().split('-')[0];

const LANGUAGE_FLAGS: Record<string, string> = {
  en: '🇺🇸',
  uk: '🇺🇦',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  it: '🇮🇹',
  pt: '🇵🇹',
};

const toUserInitials = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return 'US';
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]?.charAt(0) ?? ''}${parts[1]?.charAt(0) ?? ''}`.toUpperCase();
  }

  return trimmed.replace(/[^a-zA-Z0-9\u0400-\u04FF]/g, '').slice(0, 2).toUpperCase() || 'US';
};

const Header = () => {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN';
  const walletSummary = useAppSelector(selectWalletSummary);
  const walletBalance = useAppSelector(selectWalletBalance);
  // Only prompt confirmation if there are meaningful ITEMS (ignore shipping address)
  const hasIncompleteOrder = useAppSelector(selectHasMeaningfulItems);
  const location = useLocation();
  const { data: localeConfig, labelsByCode } = useTranslationLocales();

  const availableLanguages = useMemo(() => {
    const fromApi = localeConfig && labelsByCode
      ? [
          {
            code: normalizeLanguageCode(localeConfig.fallback.code),
            name: labelsByCode[localeConfig.fallback.code] ?? 'English',
          },
          ...localeConfig.supported.map((entry) => ({
            code: normalizeLanguageCode(entry.code),
            name: labelsByCode[entry.code] ?? entry.code.toUpperCase(),
          })),
        ]
      : [];

    const entries = [...DEFAULT_LANGUAGE_OPTIONS, ...fromApi];

    return entries.filter((item, index, array) => index === array.findIndex(candidate => candidate.code === item.code));
  }, [labelsByCode, localeConfig]);

  const activeLanguageCode = useMemo(() => {
    return normalizeLanguageCode(i18n.resolvedLanguage ?? i18n.language ?? 'en');
  }, [i18n.language, i18n.resolvedLanguage]);

  const activeLanguageFlag = LANGUAGE_FLAGS[activeLanguageCode] ?? null;
  const shouldUseNeutralLanguageIcon = activeLanguageCode === 'ru' || activeLanguageFlag == null;

  const studioCanvas = useMemo(() => {
    if (location.pathname !== '/studio') {
      return null;
    }

    return new URLSearchParams(location.search).get('canvas');
  }, [location.pathname, location.search]);

  const isIntegrationCanvas = studioCanvas === WORKSPACE_CANVAS_MODES.etsySetup
    || studioCanvas === WORKSPACE_CANVAS_MODES.etsyImport
    || studioCanvas === WORKSPACE_CANVAS_MODES.etsyOrders;

  const shouldShowStudioLink = location.pathname !== '/studio' || isIntegrationCanvas;
  const navigationItems = [
    location.pathname !== '/' ? { to: '/', label: t('nav.home') } : null,
    shouldShowStudioLink ? { to: '/studio', label: t('nav.studio', 'Studio') } : null,
    location.pathname !== '/pricing' ? { to: '/pricing', label: t('nav.pricing', 'Pricing') } : null,
    location.pathname !== '/discounts' ? { to: '/discounts', label: t('nav.discounts', 'Discounts') } : null,
    location.pathname !== '/contact' ? { to: '/contact', label: t('nav.contact', 'Contact') } : null,
  ].filter(Boolean) as Array<{ to: string; label: string }>;

  useEffect(() => {
    if (isAuthenticated && walletSummary.status === 'idle') {
      dispatch(loadWalletSummary());
    }
  }, [dispatch, isAuthenticated, walletSummary.status]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;

    if (isMobileMenuOpen) {
      body.style.overflow = 'hidden';
    }

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  const walletAmountLabel = isAuthenticated
    ? new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(walletBalance ?? 0)
    : null;
  const userMenuInitial = toUserInitials(user?.name || user?.email);
  const userMenuLabel = user?.name || user?.email || t('nav.account', 'Account');

  // No debug logging in production

  const handleLogout = async () => {
    try {
      analytics.track('button: ui.logout_click');
      await dispatch(logoutAndClear()).unwrap();
      persistor.purge();
      navigate('/');
    } catch (error) {
      logging.error('Failed to logout', { error });
    }
  };

  const changeLanguage = async (lng: string) => {
    const normalizedLanguage = normalizeLanguageCode(lng);
    await i18n.changeLanguage(normalizedLanguage);
    dispatch(setUserLanguage(normalizedLanguage));
    if (isAuthenticated) {
      try {
        await dispatch(updatePreferredLanguage(normalizedLanguage)).unwrap();
      } catch (error) {
        logging.warn('Failed to persist preferred language', { error, language: normalizedLanguage });
      }
    }
    analytics.track('dropdown: ui.language_change', { language: normalizedLanguage });
  };

  return (
    <header className="relative z-30 bg-background shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-3 py-3 sm:py-4">
          <div className="flex-shrink-0">
            <Link to="/">
              <img 
                src={theme === 'dark' ? LogoWhite : Logo} 
                alt="Print & Artisan Production Studio" 
                className="h-12 w-12 object-contain sm:h-16 sm:w-16" 
              />
            </Link>
          </div>
          <nav className="hidden lg:flex space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="text-base font-medium text-muted-foreground hover:text-foreground"
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="relative z-10 flex shrink-0 min-w-0 items-center justify-end gap-1.5 sm:gap-2 md:gap-4">
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="inline-flex h-9 gap-2 px-2 sm:px-3">
                    <Shield className="h-4 w-4" />
                    <span className="hidden sm:inline">Admin</span>
                    <span className="sr-only sm:hidden">Admin</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_orders_workspace_click');
                      navigate('/admin/orders');
                    }}
                  >
                    Orders workspace
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_import_kb_click');
                      navigate('/admin/knowledge-base');
                    }}
                  >
                    Import data to vector DB
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_user_activity_click');
                      navigate('/admin/user-activity');
                    }}
                  >
                    User activity
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_translations_click');
                      navigate('/admin/translations');
                    }}
                  >
                    Translations
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_exchange_rates_click');
                      navigate('/admin/exchange-rates');
                    }}
                  >
                    Exchange rates
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_novapost_cache_click');
                      navigate('/admin/novapost-cache');
                    }}
                  >
                    Nova Post cache
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_telegram_click');
                      navigate('/admin/telegram');
                    }}
                  >
                    Telegram
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_creator_applications_click');
                      navigate('/admin/creator-applications');
                    }}
                  >
                    Creator applications
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_promotions_click');
                      navigate('/admin/promotions');
                    }}
                  >
                    Promotions
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_paper_types_click');
                      navigate('/admin/paper-types');
                    }}
                  >
                    Paper types
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_payments_click');
                      navigate('/admin/payments');
                    }}
                  >
                    Payments
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_packaging_materials_click');
                      navigate('/admin/packaging-materials');
                    }}
                  >
                    Packaging materials
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      analytics.track('dropdown: ui.admin_homepage_slider_click');
                      navigate('/admin/homepage-slider');
                    }}
                  >
                    Homepage slider
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {isAuthenticated && location.pathname !== '/wallet' && (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="inline-flex h-9 items-center gap-2 px-2 sm:px-3"
                trackName="wallet.header_click"
              >
                <Link
                  to="/wallet"
                  className="flex items-center gap-2"
                  onClick={() => analytics.track('link: ui.wallet_nav_click', { location: 'header_balance' })}
                >
                  <Wallet className="h-4 w-4" />
                  <span className="sr-only">{t('nav.wallet', 'Wallet')}</span>
                  {walletSummary.status === 'loading' && !walletSummary.data ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <span className="text-xs sm:text-sm">{walletAmountLabel}</span>
                  )}
                </Link>
              </Button>
            )}
            {isAuthenticated && location.pathname === '/wallet' && (
              <Button
                variant="outline"
                size="sm"
                className="inline-flex h-9 items-center gap-2 px-2 sm:px-3"
                disabled
              >
                <Wallet className="h-4 w-4" />
                <span className="sr-only">{t('nav.wallet', 'Wallet')}</span>
                {walletSummary.status === 'loading' && !walletSummary.data ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <span className="text-xs sm:text-sm">{walletAmountLabel}</span>
                )}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="hidden h-9 w-9 sm:h-10 sm:w-10 md:inline-flex">
                  <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                  <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                  <span className="sr-only">Toggle theme</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setTheme('light');
                    analytics.track('dropdown: ui.theme_change', { theme: 'light' });
                  }}
                >
                  Light
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setTheme('dark');
                    analytics.track('dropdown: ui.theme_change', { theme: 'dark' });
                  }}
                >
                  Dark
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 text-base sm:h-10 sm:w-10 sm:text-lg">
                  {shouldUseNeutralLanguageIcon ? (
                    <Globe aria-hidden="true" className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <span aria-hidden="true">{activeLanguageFlag}</span>
                  )}
                  <span className="sr-only">Change language</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {availableLanguages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    disabled={activeLanguageCode === normalizeLanguageCode(lang.code)}
                  >
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="icon"
              className="relative h-9 w-9 lg:hidden"
              aria-label={t('nav.menu', 'Menu')}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
              onClick={() => setIsMobileMenuOpen((open) => !open)}
            >
              <span className="relative block h-4 w-5">
                <span
                  className={`absolute left-0 top-0 block h-0.5 w-5 origin-center rounded-full bg-foreground transition-all duration-300 ease-out ${
                    isMobileMenuOpen ? 'top-[7px] rotate-45' : ''
                  }`}
                />
                <span
                  className={`absolute left-0 top-[7px] block h-0.5 w-5 origin-center rounded-full bg-foreground transition-all duration-300 ease-out ${
                    isMobileMenuOpen ? 'scale-x-0 opacity-0' : 'scale-x-100 opacity-100'
                  }`}
                />
                <span
                  className={`absolute left-0 top-[14px] block h-0.5 w-5 origin-center rounded-full bg-foreground transition-all duration-300 ease-out ${
                    isMobileMenuOpen ? 'top-[7px] -rotate-45' : ''
                  }`}
                />
              </span>
            </Button>
            {isAuthenticated && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative h-10 w-10 rounded-full border border-border/60 bg-card p-0 hover:bg-accent sm:h-11 sm:w-11"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-brand-green text-xs font-semibold text-white">
                        {userMenuInitial}
                      </AvatarFallback>
                    </Avatar>
                    <span className="absolute -bottom-0.5 right-0 text-[9px] font-semibold leading-none text-foreground">
                      ▼
                    </span>
                    <span className="sr-only">{t('nav.account', 'Account')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuLabel className="flex flex-col gap-0.5">
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t('nav.account', 'Account')}
                    </span>
                    <span className="truncate text-sm font-semibold text-foreground">
                      {userMenuLabel}
                    </span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      onClick={() => {
                        analytics.track('dropdown: ui.integrations_menu_open', { location: 'header_user_menu' });
                      }}
                    >
                      <PlugZap className="h-4 w-4" />
                      {t('nav.integrations', 'Integrations')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        onClick={() => {
                          analytics.track('dropdown: ui.integrations_etsy_click', { location: 'header_user_menu' });
                          navigate(`/studio?canvas=${WORKSPACE_CANVAS_MODES.etsySetup}`);
                        }}
                      >
                        <Store className="h-4 w-4" />
                        Etsy
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          analytics.track('dropdown: ui.integrations_ai_artist_click', { location: 'header_user_menu' });
                          navigate('/integrations/ai-artist');
                        }}
                      >
                        <Palette className="h-4 w-4" />
                        {t('integrations.aiArtist.title', 'AI Artist app')}
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  {location.pathname !== '/wallet' && (
                    <DropdownMenuItem
                      onClick={() => {
                        analytics.track('dropdown: ui.wallet_nav_click', { location: 'header_user_menu' });
                        navigate('/wallet');
                      }}
                    >
                      <Wallet className="h-4 w-4" />
                      {t('nav.wallet', 'Wallet')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('nav.theme', 'Theme')}
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => {
                      setTheme('light');
                      analytics.track('dropdown: ui.theme_change', { location: 'header_user_menu', theme: 'light' });
                    }}
                  >
                    <Sun className="h-4 w-4" />
                    {t('theme.light', 'Light')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setTheme('dark');
                      analytics.track('dropdown: ui.theme_change', { location: 'header_user_menu', theme: 'dark' });
                    }}
                  >
                    <Moon className="h-4 w-4" />
                    {t('theme.dark', 'Dark')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('nav.language', 'Language')}
                  </DropdownMenuLabel>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Globe className="h-4 w-4" />
                      {t('nav.language', 'Language')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {availableLanguages.map((lang) => (
                        <DropdownMenuItem
                          key={`user-menu-${lang.code}`}
                          onClick={() => changeLanguage(lang.code)}
                          disabled={activeLanguageCode === normalizeLanguageCode(lang.code)}
                        >
                          {lang.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuSeparator />
                  {hasIncompleteOrder ? (
                    <ConfirmAlertDialog
                      title={t('auth.logout.title', 'Confirm logout')}
                      description={t('auth.logout.confirmLoseOrder', 'You have an unfinished order. Logging out will discard it. Continue?')}
                      confirmText={t('common.confirm', 'Confirm')}
                      cancelText={t('common.cancel', 'Cancel')}
                      onConfirm={async () => {
                        analytics.track('dialog: ui.logout_confirm');
                        await handleLogout();
                      }}
                      trigger={
                        <Button
                          variant="ghost"
                          className="w-full justify-start px-2 py-1.5 text-sm font-normal"
                          onClick={() => analytics.track('button: ui.logout_click')}
                        >
                          <UserRound className="h-4 w-4" />
                          {t('nav.logout', 'Logout')}
                        </Button>
                      }
                    />
                  ) : (
                    <DropdownMenuItem onClick={handleLogout}>
                      <UserRound className="h-4 w-4" />
                      {t('nav.logout', 'Logout')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <div
          className={`fixed inset-0 z-40 bg-brand-green/18 backdrop-blur-[2px] transition-all duration-300 lg:hidden ${
            isMobileMenuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
          }`}
          aria-hidden={!isMobileMenuOpen}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        <aside
          id="mobile-navigation"
          className={`fixed right-3 top-20 z-50 flex max-h-[calc(100vh-6rem)] w-[min(18rem,78vw)] flex-col overflow-hidden rounded-[1.75rem] border border-brand-green/15 bg-[linear-gradient(180deg,rgba(250,247,240,0.98),rgba(255,255,255,0.94))] shadow-[0_24px_60px_rgba(37,58,43,0.22)] ring-1 ring-white/60 transition-all duration-300 ease-out dark:bg-[linear-gradient(180deg,rgba(20,28,24,0.98),rgba(17,24,39,0.96))] dark:ring-white/10 lg:hidden ${
            isMobileMenuOpen ? 'pointer-events-auto translate-x-0 opacity-100' : 'pointer-events-none translate-x-6 opacity-0'
          }`}
          aria-hidden={!isMobileMenuOpen}
        >
          <div className="border-b border-brand-green/10 px-4 pb-3 pt-4 dark:border-white/10">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-green/70 dark:text-emerald-200/70">
                  {t('nav.menu', 'Menu')}
                </div>
                <div className="mt-1 text-sm font-semibold text-foreground">
                  Tulasi Home
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full"
                aria-label={t('common.close', 'Close')}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="relative block h-3.5 w-3.5">
                  <span className="absolute left-0 top-[6px] block h-0.5 w-3.5 rotate-45 rounded-full bg-foreground" />
                  <span className="absolute left-0 top-[6px] block h-0.5 w-3.5 -rotate-45 rounded-full bg-foreground" />
                </span>
              </Button>
            </div>
          </div>
          <nav className="flex flex-col gap-1 overflow-y-auto px-3 py-3">
            {navigationItems.map((item, index) => (
              <button
                key={`drawer-${item.to}`}
                type="button"
                className={`rounded-2xl border border-transparent px-3 py-3 text-left text-sm font-medium text-foreground transition-all duration-200 hover:border-brand-green/15 hover:bg-white/65 hover:pl-4 dark:hover:border-white/10 dark:hover:bg-white/5 ${
                  isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-2 opacity-0'
                }`}
                style={{ transitionDelay: isMobileMenuOpen ? `${index * 35}ms` : '0ms' }}
                onClick={() => navigate(item.to)}
              >
                {item.label}
              </button>
            ))}
            {isAuthenticated && location.pathname !== '/wallet' && (
              <button
                type="button"
                className={`rounded-2xl border border-transparent px-3 py-3 text-left text-sm font-medium text-foreground transition-all duration-200 hover:border-brand-green/15 hover:bg-white/65 hover:pl-4 dark:hover:border-white/10 dark:hover:bg-white/5 ${
                  isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-2 opacity-0'
                }`}
                style={{ transitionDelay: isMobileMenuOpen ? `${navigationItems.length * 35}ms` : '0ms' }}
                onClick={() => {
                  analytics.track('link: ui.wallet_nav_click', { location: 'mobile_menu' });
                  navigate('/wallet');
                }}
              >
                {t('nav.wallet', 'Wallet')}
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                className={`rounded-2xl border border-transparent px-3 py-3 text-left text-sm font-medium text-foreground transition-all duration-200 hover:border-brand-green/15 hover:bg-white/65 hover:pl-4 dark:hover:border-white/10 dark:hover:bg-white/5 ${
                  isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-2 opacity-0'
                }`}
                style={{ transitionDelay: isMobileMenuOpen ? `${(navigationItems.length + Number(location.pathname !== '/wallet' && isAuthenticated)) * 35}ms` : '0ms' }}
                onClick={() => navigate('/admin/orders')}
              >
                Admin
              </button>
            )}
          </nav>
        </aside>
      </div>
    </header>
  );
};

export default Header;
